import {
  ApplicationResource,
  getComponentsResourceTypeFromResource,
  getResourceId,
  isApplicationResource, isARMHybridDetail,
  isAsset,
  Resource
} from '../../../common/resource/model'
import { Asset, LogPointsConnectors } from '../../../remote-api/models'
import { ResourcesFilter, SelectorOptions } from '../../../common/selector/model'
import { EnvironmentSelectorState } from '../../../common/environment/reducer'
import { LogPointsService } from '../util/LogPointsService'
import { Dispatch, ExtraArgs, GetState } from '../../../types'
import { ResourceSelectorState } from '../../../common/resource/reducer'
import { VersionSelectorState } from '../../../common/version/reducer'
import { CONNECTORS_ICONS_PREFIX, CONNECTORS_ICONS_RESOURCE_NAME, getLogPointsResourceType, logPointsOrigin, LogPointsType } from './model'
import MonitoringLocalStorageManager from '../../../util/storage/MonitoringLocalStorageManager'
import logPointsSelectors from './selectors'
import { goToLogPoints } from '../../Sidebar/Urls'
import { storeEnvironmentsAndSelectOne } from '../../../common/environment/actions'
import {
  fetchAllResourcesAndVersions,
  fetchMainSelectorsOptionsAndSelect,
  fetchVersionsAndSelect,
  selectEnvironmentAndFetchAllResources,
  selectResourceAndFetchVersions
} from '../../../common/selector/actions'
import { LogPointsAPIAction } from '../LogPointsBody/LogPointsAPIsBody/actions'
import { fetchLoadingResources, storeResourcesAndSelectOne } from '../../../common/resource/actions'
import { selectVersion, storeVersionsAndSelectOne } from '../../../common/version/actions'
import { LogPointsApplicationsAction } from '../LogPointsBody/LogPointsApplicationsBody/actions'
import { FetchAction } from '../../../common/redux/fetch/model'
import fetchActionCreatorsFor from '../../../common/redux/fetch/actionCreatorsFor'
import { fetchLoaderHelper } from '../../../common/redux/loader/thunksFor'
import { changePlatformToken, selectors as authSelectors } from '../../../lifecycle/auth'
import { ResourceType } from '@mulesoft/monitoring-center-components/lib/models/resource'
import { ExternalValueProps } from './LogPointsPage'

export enum ActionType {
  SetStateFromLanding = 'Monitoring/LogPoints/SetStateFromLanding',
  FetchLogPointsResources = 'Monitoring/LogPoints/FetchLogPointsResources'
}

// Basic Actions

export type SetStateFromLandingAction = {
  type: ActionType.SetStateFromLanding
  payload: {
    environmentSelector: EnvironmentSelectorState,
    resourceSelector: ResourceSelectorState,
    versionSelector: VersionSelectorState
  }
}

export type FetchLogPointsResourcesAction = {
  type: ActionType.FetchLogPointsResources
  payload: {
    resources: Resource[],
  }
}

export type ConnectorsIconAction = FetchAction<LogPointsConnectors>

export type LogPointsAction =
  | SetStateFromLandingAction
  | FetchLogPointsResourcesAction
  | LogPointsAPIAction
  | LogPointsApplicationsAction
  | ConnectorsIconAction

// Basic Actions Creators

export const setStateFromLandingAction = (environmentSelector: EnvironmentSelectorState,
                                          resourceSelector: ResourceSelectorState,
                                          versionSelector: VersionSelectorState): LogPointsAction => ({
  type: ActionType.SetStateFromLanding,
  payload: {
    environmentSelector: environmentSelector,
    resourceSelector: resourceSelector,
    versionSelector: versionSelector
  }
})

export const fetchLogPointsResourcesAction = (resources: Resource[]): LogPointsAction => ({
  type: ActionType.FetchLogPointsResources,
  payload: {
    resources: resources
  }
})

export const connectorsIconsActionCreators = () => {
  return {
    ...fetchActionCreatorsFor<LogPointsConnectors>(CONNECTORS_ICONS_RESOURCE_NAME, CONNECTORS_ICONS_PREFIX, logPointsOrigin)
  }
}

// Thunks

export const fetchConnectorsIcons = () => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  return fetchLoaderHelper<LogPointsConnectors>(dispatch, connectorsIconsActionCreators(), async () => {
    return api.ui(getState)
      .fetchLogPointsConnectors()
      .then(response => response)
  })
}

export const logPointsOptions = (dispatch: Dispatch, options: SelectorOptions) =>
  ({...options, filters: {resources: dispatch(resourcesFilterWithAPIToken()) as ResourcesFilter}})

export const viewLogPoints = (props: ExternalValueProps) => (dispatch: Dispatch, getState: GetState) => {
  const rightResourceType = (res: Resource) => {
    if (props.type === LogPointsType.API) return isAsset(res)
    else return isApplicationResource(res)
  }
  const resourceSelector = logPointsSelectors.resourceSelector.getAll(getState())
  const resource = resourceSelector.resources && resourceSelector.resources.find((resource: Resource) =>
    getResourceId(resource) === props.resourceId && rightResourceType(resource)
  )

  const lsLogPointsData = MonitoringLocalStorageManager.get(logPointsOrigin)

  if (lsLogPointsData) {
    const value = {
      ...lsLogPointsData,
      selectedEnvironment: props.environmentId,
      selectedResource: props.resourceId,
      selectedVersion: props.versionId || null,
      runtimeSupport: null
    }

    MonitoringLocalStorageManager.put('logPoints', value)
  }

  if (resource) goToLogPoints(props.environmentId, resource, props.versionId)
}

export const load = (props: ExternalValueProps) => (dispatch: Dispatch, getState: GetState) => {
  const {environmentSelector, versionSelector} = getState().monitoring.logPoints
  const resourceType = getLogPointsResourceType(props)
  const resourceSelector = logPointsSelectors.resourceSelector.getAll(getState())
  const environment = environmentSelector.resources && environmentSelector.resources.find(env => env.id === props.environmentId)
  const resource = resourceSelector.resources && resourceSelector.resources.find((resource: Resource) =>
    getResourceId(resource) === props.resourceId && getComponentsResourceTypeFromResource(resource) === resourceType
  )
  const version = props.versionId && versionSelector.resources && versionSelector.resources.find(version => version.id.toString() === props.versionId)
  const options = logPointsOptions(dispatch, {origin: logPointsOrigin, targetId: props.targetId, resourceType})

  // check if selected environment is in store
  if (environment && environmentSelector.resources) {
    dispatch(storeEnvironmentsAndSelectOne(environment, environmentSelector.resources, logPointsOrigin))

    // check if selected resource is in store
    if (resource && resourceSelector.resources) {
      dispatch(fetchLoadingResources(options, props.environmentId, resourceSelector))
      dispatch(storeResourcesAndSelectOne(resource, resourceSelector.resources, logPointsOrigin))

      // check if selected version is in store
      if (version && versionSelector.resources) {
        dispatch(storeVersionsAndSelectOne(version, versionSelector.resources, logPointsOrigin))
      } else {
        dispatch(fetchVersionsAndSelect(options, resource, props.versionId))
      }
    } else { // the selected resource is not in the store, fetch all resources
      dispatch(selectVersion(options, [], undefined))
      dispatch(fetchAllResourcesAndVersions({...options, shouldSelect: true}, environment, props.resourceId, props.versionId))
    }
  } else { // the selected environment is not in the store, fetch all environments
    dispatch(selectVersion(options, [], undefined))
    dispatch(fetchMainSelectorsOptionsAndSelect(options, props.environmentId, props.resourceId, props.versionId))
  }
}

export const resourcesFilterWithAPIToken = () => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): ResourcesFilter => {
  return (resources: Resource[], envId: string) => {
    const maybePlatformToken = authSelectors.getPlatformToken(getState())

    if (maybePlatformToken) {
      return resourcesFilter(resources, envId, dispatch, getState, {api: api})
    } else {
      const apiPlatformRemoteAPI = api.apiPlatform(getState)
      return apiPlatformRemoteAPI.getAPIPlatformToken()
        .then(session => {
          dispatch(changePlatformToken(session.token))
          return resourcesFilter(resources, envId, dispatch, getState, {api: api})
        })
    }
  }
}

const resourcesFilter = (resources: Resource[], envId: string, dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  const orgId = getState().monitoring.auth.profile.organizationId
  const remoteAPIManager = api.apiManager(getState)
  const remoteARM = api.arm(getState)
  const logPointsService = new LogPointsService(remoteAPIManager, remoteARM)
  const apps = resources.filter(resource => isApplicationResource(resource)) as ApplicationResource[]
  const assets = resources.filter(resource => isAsset(resource)) as Asset[]

  if (assets.length > 0) {
    return assetsFilter(logPointsService, orgId, envId, assets).then(filteredAssets => {
      const filteredApps = appsFilter(logPointsService, apps)
      return [...filteredApps, ...filteredAssets]
    })
  } else {
    return Promise.resolve(appsFilter(logPointsService, apps))
  }
}

export const assetsFilter = (logPointsService: LogPointsService, orgId: string, envId: string, assets: Asset[]): Promise<Asset[]> => {
  const eventualRuntimesSupport = logPointsService.fetchRuntimesSupport(orgId)

  return eventualRuntimesSupport
    .then(runtimeSupport => {
      return logPointsService.fetchAssetsDetails(orgId, envId, assets)
        .then(details => {
          return logPointsService.validateAssetsCompatibility(assets, details, runtimeSupport)
            .then(validAssets => validAssets)
        })
    })
    .catch(() => [])
}

export const appsFilter = (logPointsService: LogPointsService, apps: ApplicationResource[]): ApplicationResource[] => {
  if (window.mc.featureFlags.logPoints.applications.enabled === 'true') {
    return LogPointsService.validateApplicationsCompatibility(apps)
  } else {
    return []
  }
}

export const environmentSelected = (environmentId: string) => (dispatch: Dispatch, getState: GetState) => {
  const environmentSelector = logPointsSelectors.environmentSelector.getAll(getState())
  const environments = environmentSelector.resources
  dispatch(selectEnvironmentAndFetchAllResources(logPointsOptions(dispatch, {origin: logPointsOrigin}), environmentId, environments))
}

export const resourceSelected = (resourceId: string, resourceType: ResourceType) => (dispatch: Dispatch, getState: GetState) => {
  const resourceSelector = logPointsSelectors.resourceSelector.getAll(getState())
  const resources = resourceSelector.resources
  dispatch(selectResourceAndFetchVersions(logPointsOptions(dispatch, {origin: logPointsOrigin, resourceType}), resourceId, resources))
}

export const versionSelected = (versionId: string) => (dispatch: Dispatch, getState: GetState) => {
  const versionSelector = logPointsSelectors.versionSelector.getAll(getState())
  const versions = versionSelector.resources || []
  dispatch(selectVersion(logPointsOptions(dispatch, {origin: logPointsOrigin}), versions, versionId))
}
